import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail,
  MapPin,
  Clock,
  ArrowUpRight,
  Send,
  Loader2,
  Phone,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOOGLE_REVIEWS_URL =
  "https://www.google.com/search?sca_esv=a34ddc620dd62935&rlz=1C5OZZY_enFR1219FR1219&sxsrf=APpeQnt2riNpk_pTrJbhn1UqDTnzaSn1Fg:1782122123241&q=Breizh+Boost+Studio&si=APenkKm7iecQ4G6P-TsbSMFKIQtv3EFIqRAFw-i8uEbk55Z-_yHpA4Hr45a22syT-iVfZIMXXjO83BK0mAy5WtY6ao3gPcuO_o1c6sQDLs3z39ASY773mvM%3D&uds=AJ5uw18zeF8z_P7IQbfeR8tV0KKTVEHQ-UDoyhKZ227l_NENuMs2YDFLKz1BGr_8iOAv4agt6tgYa5rKQfPRmEwobRlCU7t7NGO66tnigRniEAIGPxoTx6E&sa=X&sqi=2&ved=2ahUKEwjtoeigypqVAxUHRqQEHbz7NiYQ3PALegQINBAF";

function GoogleGIcon({ className = "h-8 w-8" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden focusable="false">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.094 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
    </svg>
  );
}

const besoins = [
  "Création d'un site internet",
  "Améliorer ma visibilité sur Google",
  "Identité visuelle (logo, supports)",
  "Mettre en place la prise de rendez-vous",
  "Plusieurs choses à la fois",
  "Juste échanger pour comprendre",
];

export default function Contact() {
  const [form, setForm] = useState({
    nom: "",
    entreprise: "",
    activite: "",
    besoin: "",
    budget: "",
    email: "",
    telephone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Nom, email et message sont obligatoires.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      if (data.email === "sent") {
        toast.success("Message envoyé ! Je reviens vers vous rapidement.");
      } else if (data.email === "skipped_no_api_key" || data.status === "ok") {
        toast.success("Votre demande a bien été reçue.");
      } else {
        toast.success("Votre demande est enregistrée.");
      }
      setForm({
        nom: "",
        entreprise: "",
        activite: "",
        besoin: "",
        budget: "",
        email: "",
        telephone: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="container-up pt-16 md:pt-24 pb-16">
        <div className="grid md:grid-cols-12 gap-10 items-end reveal">
          <div className="md:col-span-8">
            <span className="section-label">Contact</span>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              Parlons de
              <em className="italic font-light"> votre projet</em>.
            </h1>
          </div>
          <p className="md:col-span-4 text-up-muted leading-relaxed md:pb-2">
            Un premier échange découverte de 30 minutes, offert, pour poser les
            bases ensemble.
          </p>
        </div>
      </section>

      <section className="container-up pb-24 md:pb-32">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* FORM */}
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="lg:col-span-8 rounded-3xl border border-up-taupe/40 bg-up-offwhite p-6 md:p-10 space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <Field
                label="Nom"
                required
                name="nom"
                value={form.nom}
                onChange={handleChange}
                testId="contact-input-nom"
              />
              <Field
                label="Email"
                required
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                testId="contact-input-email"
              />
              <Field
                label="Entreprise"
                name="entreprise"
                value={form.entreprise}
                onChange={handleChange}
                testId="contact-input-entreprise"
              />
              <Field
                label="Activité"
                name="activite"
                value={form.activite}
                onChange={handleChange}
                testId="contact-input-activite"
              />
              <SelectField
                label="Votre besoin"
                name="besoin"
                value={form.besoin}
                onChange={handleChange}
                options={besoins}
                testId="contact-select-besoin"
              />
              <Field
                label="Téléphone"
                type="tel"
                name="telephone"
                value={form.telephone}
                onChange={handleChange}
                testId="contact-input-telephone"
              />
            </div>

            <div>
              <label className="block text-xs tracking-[0.22em] uppercase text-up-muted mb-2">
                Message <span className="text-up-brown">*</span>
              </label>
              <textarea
                name="message"
                rows={6}
                value={form.message}
                onChange={handleChange}
                data-testid="contact-input-message"
                placeholder="Parlez-moi de votre activité, de votre projet, de ce qui vous amène ici…"
                className="w-full bg-transparent border-b border-up-taupe focus:border-up-brown outline-none py-3 text-up-brown placeholder:text-up-muted/70 resize-none"
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-up-taupe/30">
              <p className="text-xs text-up-muted max-w-sm">
                En envoyant ce formulaire, vous acceptez que vos informations
                soient utilisées uniquement pour répondre à votre demande.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="btn-primary group self-start sm:self-auto disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Envoi…
                  </>
                ) : (
                  <>
                    Envoyer ma demande
                    <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </button>
            </div>
          </form>

          {/* SIDE INFO */}
          <aside className="lg:col-span-4 flex flex-col gap-5">
            <div className="rounded-3xl border border-up-taupe/40 bg-up-sand p-8 flex flex-col gap-4">
              <span className="section-label">Réserver un appel</span>
              <h3 className="font-serif text-3xl leading-tight">
                Appel découverte <em className="italic font-light">30 min</em>, offert.
              </h3>
              <p className="text-sm text-up-muted leading-relaxed">
                On échange sur votre activité, vos besoins et on voit ensemble
                la meilleure façon d&apos;avancer.
              </p>
              <Link
                to="/contact"
                className="btn-primary !px-6 !py-3 group self-start"
                data-testid="contact-rdv-btn"
              >
                Prendre rendez-vous
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </div>

            <div className="rounded-3xl border border-up-taupe/40 bg-up-offwhite p-8 flex flex-col gap-5">
              <div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-up-muted shrink-0" />
                  <p className="text-xs tracking-[0.22em] uppercase text-up-muted">Email</p>
                </div>
                <a
                  href="mailto:breizh.boost.studio@gmail.com"
                  className="block mt-2 font-serif text-[13px] md:text-sm whitespace-nowrap hover:text-up-brown-soft transition-colors"
                  data-testid="contact-email-link"
                >
                  breizh.boost.studio@gmail.com
                </a>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="h-5 w-5 text-up-muted mt-0.5" />
                <div>
                  <p className="text-xs tracking-[0.22em] uppercase text-up-muted">Téléphone</p>
                  <a
                    href="tel:0695977072"
                    className="block mt-1 font-serif text-xl hover:text-up-brown-soft transition-colors"
                    data-testid="contact-phone-link"
                    aria-label="Appeler Breizh Boost Studio"
                  >
                    Appeler
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="h-5 w-5 text-up-muted mt-0.5" />
                <div>
                  <p className="text-xs tracking-[0.22em] uppercase text-up-muted">Studio</p>
                  <p className="font-serif text-xl">Bretagne, France</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock className="h-5 w-5 text-up-muted mt-0.5" />
                <div>
                  <p className="text-xs tracking-[0.22em] uppercase text-up-muted">Réponse</p>
                  <p className="font-serif text-xl">Sous 48h ouvrées</p>
                </div>
              </div>

              <div className="pt-4 mt-2 border-t border-up-taupe/30">
                <p className="text-xs tracking-[0.22em] uppercase text-up-muted">
                  Suivez Breizh Boost Studio
                </p>
                <div className="mt-4 flex items-center gap-5">
                  <a
                    href="https://www.instagram.com/breizh.boost.studio/?hl=fr"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram Breizh Boost Studio"
                    data-testid="contact-instagram-link"
                    className="inline-flex transition-all duration-300 hover:scale-110 hover:opacity-90"
                  >
                    <img
                      src="https://customer-assets.emergentagent.com/job_breizh-boost/artifacts/1580qcvw_Instagram_Glyph_Gradient.png"
                      alt="Instagram Breizh Boost Studio"
                      className="h-8 w-8"
                    />
                  </a>
                  <a
                    href="https://www.facebook.com/profile.php?id=61589296417284"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook Breizh Boost Studio"
                    data-testid="contact-facebook-link"
                    className="inline-flex transition-all duration-300 hover:scale-110 hover:opacity-90"
                  >
                    <img
                      src="https://customer-assets.emergentagent.com/job_breizh-boost/artifacts/dejeqq22_Facebook_Logo_Primary.png"
                      alt="Facebook Breizh Boost Studio"
                      className="h-8 w-8"
                    />
                  </a>
                  <a
                    href={GOOGLE_REVIEWS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Avis Google Breizh Boost Studio"
                    data-testid="contact-google-link"
                    className="inline-flex transition-all duration-300 hover:scale-110 hover:opacity-90"
                  >
                    <GoogleGIcon className="h-8 w-8" />
                  </a>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, type = "text", name, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-xs tracking-[0.22em] uppercase text-up-muted mb-2">
        {label} {required && <span className="text-up-brown">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="w-full bg-transparent border-b border-up-taupe focus:border-up-brown outline-none py-3 text-up-brown placeholder:text-up-muted/60"
      />
    </div>
  );
}

function SelectField({ label, name, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-xs tracking-[0.22em] uppercase text-up-muted mb-2">
        {label}
      </label>
      <div className="relative">
        <select
          name={name}
          value={value}
          onChange={onChange}
          data-testid={testId}
          className="w-full appearance-none bg-transparent border-b border-up-taupe focus:border-up-brown outline-none py-3 pr-6 text-up-brown"
        >
          <option value="">— Sélectionner —</option>
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-up-muted">
          ▾
        </span>
      </div>
    </div>
  );
}
